<template>
  <div class="timeline-wrapper">
    <div class="timeline-chart" ref="chart">
      <!-- 动态连接线：只在相邻节点之间绘制，不超出首尾节点 -->
      <svg class="timeline-svg" ref="svg">
        <line
          v-for="(seg, i) in lineSegments"
          :key="'line-' + i"
          :x1="seg.x1"
          :y1="seg.y1"
          :x2="seg.x2"
          :y2="seg.y2"
          stroke="#c8cdd6"
          stroke-width="2"
        />
      </svg>

      <div class="node-list">
        <div
          v-for="(node, index) in nodes"
          :key="node.id"
          class="node-unit"
        >
          <!-- 上方子节点 -->
          <div class="sub-nodes top">
            <div
              v-if="node.topChildren && node.topChildren.length"
              class="sub-area"
            >
              <div
                v-for="(child, ci) in node.topChildren"
                :key="'top-' + ci"
                class="sub-item"
              >
                <span class="sub-item-dot"></span>
                <span class="sub-item-label">{{ child }}</span>
              </div>
            </div>
          </div>

          <!-- 主节点 -->
          <div
            :class="['main-node', node.type]"
            :ref="'node-' + index"
          >
            {{ node.label }}
          </div>

          <!-- 下方子节点 -->
          <div class="sub-nodes bottom">
            <div
              v-if="node.bottomChildren && node.bottomChildren.length"
              class="sub-area"
            >
              <div
                v-for="(child, ci) in node.bottomChildren"
                :key="'bot-' + ci"
                class="sub-item"
              >
                <span class="sub-item-dot"></span>
                <span class="sub-item-label">{{ child }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TimelineFlowchart',
  props: {
    value: {
      type: Array,
      default: null
    }
  },
  data() {
    return {
      lineSegments: [],
      defaultNodes: [
        {
          id: 'start',
          label: '开始',
          type: 'start',
          topChildren: [],
          bottomChildren: []
        },
        {
          id: 'step1',
          label: '前期采购资料',
          type: 'step',
          topChildren: ['子主题 1', '子主题 2', '子主题 3', '子主题 4'],
          bottomChildren: []
        },
        {
          id: 'step2',
          label: '会议纪要',
          type: 'step',
          topChildren: [],
          bottomChildren: ['子主题 1', '子主题 2', '子主题 3', '子主题 4']
        },
        {
          id: 'step3',
          label: '阳光采购审批单',
          type: 'step',
          topChildren: ['子主题 1', '子主题 2'],
          bottomChildren: []
        },
        {
          id: 'step4',
          label: '招标公告',
          type: 'step',
          topChildren: [],
          bottomChildren: ['子主题 1']
        },
		{
		  id: 'step5',
		  label: '中标公告',
		  type: 'step',
		  topChildren: ['子主题 1'],
		  bottomChildren: []
		},
		{
		  id: 'step6',
		  label: '采购过程纪要',
		  type: 'step',
		  topChildren: [],
		  bottomChildren: ['子主题 1']
		},
		{
		  id: 'step7',
		  label: '合同审批单',
		  type: 'step',
		  topChildren: ['子主题 1'],
		  bottomChildren: []
		},
		{
		  id: 'step8',
		  label: '合同',
		  type: 'step',
		  topChildren: [],
		  bottomChildren: ['子主题 1']
		},
        {
          id: 'end',
          label: '结束',
          type: 'end',
          topChildren: [],
          bottomChildren: []
        }
      ]
    }
  },
  computed: {
    nodes() {
      return this.value || this.defaultNodes
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.calcLines()
    })
    window.addEventListener('resize', this.calcLines)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calcLines)
  },
  methods: {
    calcLines() {
      const chartEl = this.$refs.chart
      if (!chartEl) return
      const chartRect = chartEl.getBoundingClientRect()

      const segments = []

      for (let i = 0; i < this.nodes.length - 1; i++) {
        const leftRefs = this.$refs['node-' + i]
        const rightRefs = this.$refs['node-' + (i + 1)]
        // $refs 返回数组（因为在 v-for 中）
        const leftEl = Array.isArray(leftRefs) ? leftRefs[0] : leftRefs
        const rightEl = Array.isArray(rightRefs) ? rightRefs[0] : rightRefs
        if (!leftEl || !rightEl) continue

        const leftRect = leftEl.getBoundingClientRect()
        const rightRect = rightEl.getBoundingClientRect()

        // 从左节点右边缘中心 → 右节点左边缘中心
        segments.push({
          x1: leftRect.right - chartRect.left,
          y1: leftRect.top - chartRect.top + (leftRect.height / 2) + 1,
          x2: rightRect.left - chartRect.left,
          y2: rightRect.top - chartRect.top + (rightRect.height / 2) + 1
        })
      }

      this.lineSegments = segments

      // 同步 SVG 尺寸
      const svg = this.$refs.svg
      if (svg) {
        svg.style.width = chartEl.scrollWidth + 'px'
        svg.style.height = chartEl.offsetHeight + 'px'
      }
    }
  }
}
</script>

<style scoped>
.timeline-wrapper {
  background: #fff;
  border-radius: 12px;
  padding: 60px 40px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow-x: auto;
  font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
}

.timeline-chart {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 900px;
}

/* SVG 连接线层 */
.timeline-svg {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 0;
  overflow: visible;
}

/* 节点列表 */
.node-list {
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
  z-index: 1;
}

/* 单个节点单元 */
.node-unit {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  position: relative;
}

/* 子节点区域（上/下） */
.sub-nodes {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-height: 100px;
}

.sub-nodes.top {
  justify-content: flex-end;
  padding-bottom: 8px;
}

.sub-nodes.bottom {
  justify-content: flex-start;
  padding-top: 8px;
}

/* 子节点区域（带竖线） */
.sub-area {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-left: 14px;
}

.sub-area::before {
  content: '';
  position: absolute;
  left: 0;
  top: 6px;
  bottom: 6px;
  width: 1.5px;
  background: #c8cdd6;
}

/* 子节点项 */
.sub-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 0;
  position: relative;
}

.sub-item::before {
  content: '';
  position: absolute;
  left: -14px;
  top: 50%;
  width: 14px;
  height: 1.5px;
  background: #c8cdd6;
  transform: translateY(-50%);
}

.sub-item-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #c8cdd6;
  flex-shrink: 0;
}

.sub-item-label {
  font-size: 12px;
  color: #666;
  white-space: nowrap;
}

/* 主节点 */
.main-node {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 20px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  position: relative;
  z-index: 2;
  cursor: default;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  user-select: none;
}

.main-node:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.15);
}

.main-node.start {
  background: #4caf7d;
  color: #fff;
  border: none;
  box-shadow: 0 3px 10px rgba(76, 175, 125, 0.4);
  padding: 15px 33px;
  font-size: 16px;
}

.main-node.end {
  background: red;
  color: #fff;
  border: none;
  box-shadow: 0 3px 10px rgba(76, 175, 125, 0.4);
}

.main-node.step {
  background: #fff;
  color: #444;
  border: 1.5px solid #c8cdd6;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}
</style>
